import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Save, X, Plus, Trash2, AlertCircle } from 'lucide-react';
import type { WorkflowStage, WorkflowTransition, TransitionCondition } from '@/types/dispute-types';
import { mockRoles } from '@/lib/mock-data/dispute-types-mock-data';

interface WorkflowTransitionEditorProps {
  transition: WorkflowTransition | null;
  stages: WorkflowStage[];
  open: boolean;
  onClose: () => void;
  onSave: (transition: WorkflowTransition) => void;
}

const conditionOperators = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
  { value: 'contains', label: 'Contains' },
  { value: 'in', label: 'In' },
  { value: 'not_in', label: 'Not In' },
];

export function WorkflowTransitionEditor({ 
  transition, 
  stages, 
  open, 
  onClose, 
  onSave 
}: WorkflowTransitionEditorProps) {
  const [formData, setFormData] = useState({
    name: '',
    fromStageId: '',
    toStageId: '',
    requiresComment: false,
    requiresApproval: false,
    approverRoles: [] as string[],
    autoTransition: false,
    autoTransitionDelay: 0,
  });

  const [conditions, setConditions] = useState<TransitionCondition[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (transition) {
      setFormData({
        name: transition.name,
        fromStageId: transition.fromStageId,
        toStageId: transition.toStageId,
        requiresComment: transition.requiresComment,
        requiresApproval: transition.requiresApproval,
        approverRoles: transition.approverRoles || [],
        autoTransition: transition.autoTransition,
        autoTransitionDelay: transition.autoTransitionDelay || 0,
      });
      setConditions(transition.conditions || []);
    } else {
      setFormData({
        name: '',
        fromStageId: '',
        toStageId: '',
        requiresComment: false,
        requiresApproval: false,
        approverRoles: [],
        autoTransition: false,
        autoTransitionDelay: 0,
      });
      setConditions([]);
    }
    setErrors({});
  }, [transition, open]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Transition name is required';
    }

    if (!formData.fromStageId) {
      newErrors.fromStageId = 'From stage is required';
    }

    if (!formData.toStageId) {
      newErrors.toStageId = 'To stage is required';
    }

    if (formData.fromStageId === formData.toStageId) {
      newErrors.toStageId = 'From and To stages must be different';
    }

    if (formData.requiresApproval && formData.approverRoles.length === 0) {
      newErrors.approverRoles = 'At least one approver role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }

    const savedTransition: WorkflowTransition = {
      id: transition?.id || '',
      name: formData.name,
      fromStageId: formData.fromStageId,
      toStageId: formData.toStageId,
      requiresComment: formData.requiresComment,
      requiresApproval: formData.requiresApproval,
      approverRoles: formData.approverRoles,
      autoTransition: formData.autoTransition,
      autoTransitionDelay: formData.autoTransitionDelay,
      conditions: conditions.length > 0 ? conditions : undefined,
    };

    onSave(savedTransition);
  };

  const toggleRole = (roleId: string) => {
    const roles = formData.approverRoles;
    if (roles.includes(roleId)) {
      setFormData({ ...formData, approverRoles: roles.filter(r => r !== roleId) });
    } else {
      setFormData({ ...formData, approverRoles: [...roles, roleId] });
    }
  };

  const addCondition = () => {
    const newCondition: TransitionCondition = {
      id: `cond-${Date.now()}`,
      field: '',
      operator: 'equals',
      value: '',
    };
    setConditions([...conditions, newCondition]);
  };

  const updateCondition = (id: string, updates: Partial<TransitionCondition>) => {
    setConditions(conditions.map(c => c.id === id ? { ...c, ...updates } : c));
  };

  const removeCondition = (id: string) => {
    setConditions(conditions.filter(c => c.id !== id));
  };

  const getStageColor = (stageId: string) => {
    const stage = stages.find(s => s.id === stageId);
    return stage?.color || '#3B82F6';
  };

  const getStageName = (stageId: string) => {
    const stage = stages.find(s => s.id === stageId);
    return stage?.name || 'Unknown Stage';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {transition ? 'Edit Transition' : 'Add Transition'}
          </DialogTitle>
          <DialogDescription>
            Configure how disputes move between workflow stages
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Basic Information</h3>
              
              <div className="space-y-2">
                <Label htmlFor="transition-name">
                  Transition Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="transition-name"
                  placeholder="e.g., Approve and Move to Next Stage"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={errors.name ? 'border-destructive' : ''}
                />
                {errors.name && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.name}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="from-stage">
                    From Stage <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.fromStageId}
                    onValueChange={(value) => setFormData({ ...formData, fromStageId: value })}
                  >
                    <SelectTrigger id="from-stage" className={errors.fromStageId ? 'border-destructive' : ''}>
                      <SelectValue placeholder="Select stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {stages.map((stage) => (
                        <SelectItem key={stage.id} value={stage.id}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: stage.color }}
                            />
                            {stage.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.fromStageId && (
                    <p className="text-sm text-destructive flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.fromStageId}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="to-stage">
                    To Stage <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.toStageId}
                    onValueChange={(value) => setFormData({ ...formData, toStageId: value })}
                  >
                    <SelectTrigger id="to-stage" className={errors.toStageId ? 'border-destructive' : ''}>
                      <SelectValue placeholder="Select stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {stages.map((stage) => (
                        <SelectItem key={stage.id} value={stage.id}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: stage.color }}
                            />
                            {stage.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.toStageId && (
                    <p className="text-sm text-destructive flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.toStageId}
                    </p>
                  )}
                </div>
              </div>

              {formData.fromStageId && formData.toStageId && formData.fromStageId !== formData.toStageId && (
                <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Badge
                        variant="outline"
                        style={{
                          backgroundColor: `${getStageColor(formData.fromStageId)}20`,
                          borderColor: getStageColor(formData.fromStageId),
                        }}
                      >
                        {getStageName(formData.fromStageId)}
                      </Badge>
                      <span>→</span>
                      <Badge
                        variant="outline"
                        style={{
                          backgroundColor: `${getStageColor(formData.toStageId)}20`,
                          borderColor: getStageColor(formData.toStageId),
                        }}
                      >
                        {getStageName(formData.toStageId)}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Transition Requirements */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Transition Requirements</h3>
              
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <Label>Requires Comment</Label>
                  <p className="text-sm text-muted-foreground">
                    User must provide a comment when using this transition
                  </p>
                </div>
                <Switch
                  checked={formData.requiresComment}
                  onCheckedChange={(checked) => setFormData({ ...formData, requiresComment: checked })}
                />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <Label>Requires Approval</Label>
                  <p className="text-sm text-muted-foreground">
                    Transition must be approved by designated roles
                  </p>
                </div>
                <Switch
                  checked={formData.requiresApproval}
                  onCheckedChange={(checked) => setFormData({ ...formData, requiresApproval: checked })}
                />
              </div>

              {formData.requiresApproval && (
                <div className="space-y-2">
                  <Label>
                    Approver Roles <span className="text-destructive">*</span>
                  </Label>
                  <div className="space-y-2">
                    {mockRoles.map((role) => (
                      <div key={role.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`transition-approver-${role.id}`}
                          checked={formData.approverRoles.includes(role.name)}
                          onCheckedChange={() => toggleRole(role.name)}
                        />
                        <Label htmlFor={`transition-approver-${role.id}`} className="cursor-pointer">
                          {role.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                  {errors.approverRoles && (
                    <p className="text-sm text-destructive flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.approverRoles}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Auto-Transition Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Auto-Transition Settings</h3>
              
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <Label>Enable Auto-Transition</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically transition after a specified delay
                  </p>
                </div>
                <Switch
                  checked={formData.autoTransition}
                  onCheckedChange={(checked) => setFormData({ ...formData, autoTransition: checked })}
                />
              </div>

              {formData.autoTransition && (
                <div className="space-y-2">
                  <Label htmlFor="auto-delay">Delay (hours)</Label>
                  <Input
                    id="auto-delay"
                    type="number"
                    min="0"
                    value={formData.autoTransitionDelay}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      autoTransitionDelay: parseInt(e.target.value) || 0 
                    })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Hours to wait before automatically transitioning
                  </p>
                </div>
              )}
            </div>

            {/* Transition Conditions */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold">Transition Conditions</h3>
                  <p className="text-xs text-muted-foreground">
                    Optional conditions that must be met for this transition
                  </p>
                </div>
                <Button onClick={addCondition} size="sm" variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Condition
                </Button>
              </div>

              {conditions.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="pt-6 pb-6 text-center">
                    <p className="text-sm text-muted-foreground">
                      No conditions defined. This transition will always be available.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {conditions.map((condition) => (
                    <Card key={condition.id}>
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-1 space-y-3">
                            <div className="grid grid-cols-3 gap-2">
                              <div className="space-y-1">
                                <Label className="text-xs">Field</Label>
                                <Input
                                  placeholder="e.g., amount"
                                  value={condition.field}
                                  onChange={(e) => updateCondition(condition.id, { field: e.target.value })}
                                  size={1}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Operator</Label>
                                <Select
                                  value={condition.operator}
                                  onValueChange={(value: 'equals' | 'not_equals' | 'greater_than' | 'less_than' | 'contains' | 'in' | 'not_in') => updateCondition(condition.id, { operator: value })}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {conditionOperators.map((op) => (
                                      <SelectItem key={op.value} value={op.value}>
                                        {op.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Value</Label>
                                <Input
                                  placeholder={condition.operator === 'in' || condition.operator === 'not_in' ? "Comma-separated values" : "Value"}
                                  value={typeof condition.value === 'boolean' ? String(condition.value) : (Array.isArray(condition.value) ? condition.value.join(', ') : condition.value)}
                                  onChange={(e) => {
                                    const inputValue = e.target.value;
                                    // For 'in' and 'not_in' operators, parse as array
                                    if (condition.operator === 'in' || condition.operator === 'not_in') {
                                      const arrayValue = inputValue.split(',').map(v => v.trim()).filter(v => v !== '');
                                      updateCondition(condition.id, { value: arrayValue.length > 0 ? arrayValue : inputValue });
                                    } else {
                                      updateCondition(condition.id, { value: inputValue });
                                    }
                                  }}
                                  size={1}
                                />
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCondition(condition.id)}
                            className="text-destructive hover:text-destructive mt-5"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            Save Transition
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
